import { useEffect, useRef, useMemo } from 'react';
import { useGraph } from '../../../store/simulationStore';
import { useScenarioStore } from '../../../store/scenarioStore';
import type { SimulationGraph, ScenarioValidationError } from '../../../models/simulation';

/**
 * Hook to detect graph changes and validate scenarios
 * Automatically validates scenarios when graph.dataUpdatedAt changes
 */
export function useScenarioValidation() {
    const graph = useGraph();
    const validateAllScenarios = useScenarioStore((state) => state.validateAllScenarios);
    const scenarios = useScenarioStore((state) => state.scenarios);
    const validationErrors = useScenarioStore((state) => state.validationErrors);
    const isScenarioStale = useScenarioStore((state) => state.isScenarioStale);
    
    const previousDataUpdatedAt = useRef<string | null>(null);
    const previousInputDistributionsHash = useRef<string | null>(null);

    // Create a hash of input distribution configs to detect when they actually change
    const inputDistributionsHash = useMemo(() => {
        const distributionNodes = graph.nodes.filter((node) => node.data.type === 'distribution');
        const configs = distributionNodes
            .map((node) => {
                if (node.data.type === 'distribution') {
                    const config = node.data.config;
                    return `${node.id}:${JSON.stringify(config)}`;
                }
                return '';
            })
            .filter(Boolean)
            .sort()
            .join('|');
        return configs;
    }, [graph.nodes]);

    useEffect(() => {
        // Skip on initial mount
        if (previousDataUpdatedAt.current === null) {
            previousDataUpdatedAt.current = graph.dataUpdatedAt;
            previousInputDistributionsHash.current = inputDistributionsHash;
            return;
        }

        // Only validate if input distributions have actually changed
        const inputDistributionsChanged = previousInputDistributionsHash.current !== inputDistributionsHash;
        
        if (inputDistributionsChanged) {
            // Validate all scenarios against the new graph structure
            validateAllScenarios(graph);
            previousInputDistributionsHash.current = inputDistributionsHash;
        }
        
        previousDataUpdatedAt.current = graph.dataUpdatedAt;
    }, [graph.dataUpdatedAt, inputDistributionsHash, validateAllScenarios, graph]);

    // Compute stale scenarios - only mark as stale if input distributions have actually changed
    const staleScenarios = useMemo(() => {
        // Check if input distributions have changed since scenarios were last checked
        const inputDistributionsChanged = previousInputDistributionsHash.current !== null && 
            previousInputDistributionsHash.current !== inputDistributionsHash;
        
        // If input distributions haven't changed, no scenarios are stale
        if (!inputDistributionsChanged) {
            return [];
        }
        
        // Only show staleness for scenarios that were created before input distributions changed
        return scenarios.filter((scenario) => {
            // Baseline is never stale
            if (scenario.isBaseline) return false;
            
            // Scenario is stale if its graphDataUpdatedAt is different from current
            // This means it was created before the current input distributions
            return scenario.graphDataUpdatedAt !== graph.dataUpdatedAt;
        });
    }, [scenarios, graph.dataUpdatedAt, inputDistributionsHash]);

    // Compute scenarios with validation errors
    const scenariosWithErrors = scenarios.filter((scenario) => {
        const errors = validationErrors.get(scenario.id);
        return errors && errors.length > 0;
    });

    return {
        staleScenarios,
        scenariosWithErrors,
        hasStaleScenarios: staleScenarios.length > 0,
        hasValidationErrors: scenariosWithErrors.length > 0,
        getValidationErrors: (scenarioId: string): ScenarioValidationError[] => {
            return validationErrors.get(scenarioId) || [];
        },
    };
}

/**
 * Hook to validate a specific scenario against the current graph
 */
export function useValidateScenario(scenarioId: string | null) {
    const graph = useGraph();
    const validateScenario = useScenarioStore((state) => state.validateScenario);
    const validationErrors = useScenarioStore((state) => state.validationErrors);

    useEffect(() => {
        if (scenarioId) {
            validateScenario(scenarioId, graph);
        }
    }, [scenarioId, graph.dataUpdatedAt, validateScenario, graph]);

    const errors = scenarioId ? validationErrors.get(scenarioId) || [] : [];

    return {
        errors,
        hasErrors: errors.length > 0,
    };
}

